import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ShoppingBag, Upload, Users, Package, ArrowLeft, Image, DollarSign, Calendar, AlertCircle, Printer, Download, Filter, X, Edit, Trash2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import PrintOrder from '@/components/PrintOrder';
import EditUserModal from '@/components/EditUserModal';
import EditOrderModal from '@/components/EditOrderModal';
import SeasonsManager from '@/components/SeasonsManager';
import UserImport from '@/components/UserImport';
import AccountsManager from '@/components/AccountsManager';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user, onLogout }) => {
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingPendingUsers, setLoadingPendingUsers] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [uploadingPricat, setUploadingPricat] = useState(false);
  const [uploadingStock, setUploadingStock] = useState(false);
  const [rebuildingStock, setRebuildingStock] = useState(false);
  const [importingSS27, setImportingSS27] = useState(false);
  const [progSeasonsList, setProgSeasonsList] = useState([]);
  const [seasonToMigrate, setSeasonToMigrate] = useState('');
  const [migratingSeason, setMigratingSeason] = useState(false);
  const [completingImages, setCompletingImages] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTermName, setSearchTermName] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [imageUrls, setImageUrls] = useState('');
  const [searchTermPrices, setSearchTermPrices] = useState('');
  const [editingProductPrices, setEditingProductPrices] = useState(null);
  const [pricesPT, setPricesPT] = useState('');
  const [pricesPVPR, setPricesPVPR] = useState('');
  const [uploadingProgrammingPricat, setUploadingProgrammingPricat] = useState(false);
  const [programmingSeason, setProgrammingSeason] = useState('SS26');
  const [completingProgrammingImages, setCompletingProgrammingImages] = useState(false);
  const [printingOrder, setPrintingOrder] = useState(null);
  const [uploadingZipImages, setUploadingZipImages] = useState(false);
  const [programmingProducts, setProgrammingProducts] = useState([]);
  const [loadingProgrammingProducts, setLoadingProgrammingProducts] = useState(false);
  const [searchTermProgramming, setSearchTermProgramming] = useState('');
  const [searchTermProgrammingName, setSearchTermProgrammingName] = useState('');
  const [editingProgrammingProduct, setEditingProgrammingProduct] = useState(null);
  const [programmingImageUrls, setProgrammingImageUrls] = useState('');
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    business_name: '',
    phone: ''
  });

  // Filter states
  const [filterClient, setFilterClient] = useState('');
  const [filterReference, setFilterReference] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);

  // User filters
  const [filterUserName, setFilterUserName] = useState('');
  const [filterContactName, setFilterContactName] = useState('');
  const [filterUserProvince, setFilterUserProvince] = useState('');
  const [showUserFilters, setShowUserFilters] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchUsers();
    fetchPendingUsers();
  }, []);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      toast.error('Error al cargar pedidos');
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/pending-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios pendientes');
    } finally {
      setLoadingPendingUsers(false);
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/users/${userId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario aprobado exitosamente');
      fetchUsers();
      fetchPendingUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar usuario');
    }
  };

  const handleRejectUser = async (userId) => {
    if (!window.confirm('¿Estás seguro de rechazar este usuario? Será eliminado permanentemente.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/users/${userId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario rechazado y eliminado');
      fetchUsers();
      fetchPendingUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar usuario');
    }
  };

  const handleUploadPricat = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPricat(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/upload-pricat`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`PRICAT cargado: ${response.data.products_created} productos, ${response.data.variants_created} variantes creadas`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar PRICAT');
    } finally {
      setUploadingPricat(false);
      e.target.value = '';
    }
  };

  const handleUploadStock = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingStock(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/upload-stock`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(response.data.message || 'Catálogo de stock actualizado');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar stock');
    } finally {
      setUploadingStock(false);
      e.target.value = '';
    }
  };

  const handleRebuildStock = async () => {
    if (!window.confirm('Se reconstruirá el catálogo de Stock con el último CSV de la carpeta FTP, completando imágenes, EANs y precios. ¿Continuar?')) return;
    setRebuildingStock(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/sync-ftp`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message || 'Catálogo reconstruido desde FTP');
        fetchProducts();
      } else {
        toast.error(response.data.error || 'No se pudo reconstruir el catálogo');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reconstruir el catálogo desde FTP');
    } finally {
      setRebuildingStock(false);
    }
  };
  const handleImportSS27 = async () => {
    if (!window.confirm('Se importará/actualizará la temporada SS27 (105 productos). ¿Continuar?')) return;
    setImportingSS27(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/import-ss27-season`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || 'SS27 importada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar SS27');
    } finally {
      setImportingSS27(false);
    }
  };

  const loadProgSeasons = async () => {
    try {
      const res = await axios.get(`${API}/programming/seasons`);
      setProgSeasonsList(res.data.seasons || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadProgSeasons(); }, []);

  const handleMigrateSeason = async () => {
    if (!seasonToMigrate) { toast.error('Selecciona una temporada'); return; }
    if (!window.confirm(`Se pasará la temporada ${seasonToMigrate} de Programación a STOCK: sus productos dejarán de verse en Programación y aparecerán en el catálogo de Stock cuando estén en el CSV del FTP. ¿Continuar?`)) return;
    setMigratingSeason(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/migrate-season-to-stock?season=${encodeURIComponent(seasonToMigrate)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Temporada pasada a stock');
      setSeasonToMigrate('');
      loadProgSeasons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al pasar la temporada a stock');
    } finally {
      setMigratingSeason(false);
    }
  };




  const handleUploadProgrammingPricat = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingProgrammingPricat(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('season', programmingSeason);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/upload-programming-pricat`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`PRICAT ${response.data.season} procesado: ${response.data.products_created} productos creados, ${response.data.products_updated} actualizados`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar PRICAT de programación');
    } finally {
      setUploadingProgrammingPricat(false);
      e.target.value = '';
    }
  };

  const handleCompleteProgrammingImages = async () => {
    if (!window.confirm(`¿Buscar imágenes automáticamente para ${programmingSeason} desde hi-tecupdatefile.weebly.com/ss26.html?\n\nEsto puede tardar varios minutos.`)) {
      return;
    }

    setCompletingProgrammingImages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/complete-programming-images?season=${programmingSeason}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`✅ ${response.data.updated} productos actualizados con imágenes de ${response.data.checked} revisados`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al completar imágenes');
    } finally {
      setCompletingProgrammingImages(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/admin/create-user`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario creado exitosamente');
      setNewUser({ email: '', password: '', business_name: '', phone: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleEditImages = (product) => {
    setEditingProduct(product);
    setImageUrls(product.images.join('\n'));
  };

  const handleSaveImages = async () => {
    if (!editingProduct) return;

    try {
      const token = localStorage.getItem('token');
      const urls = imageUrls.split('\n').filter(url => url.trim() !== '');
      
      await axios.put(
        `${API}/admin/products/${editingProduct.id}/images`,
        urls,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success('Imágenes actualizadas exitosamente');
      setEditingProduct(null);
      setImageUrls('');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar imágenes');
    }
  };

  const handleEditPrices = (product) => {
    setEditingProductPrices(product);
    // Get prices from first variant (all have the same price)
    if (product.variants && product.variants.length > 0) {
      setPricesPT(product.variants[0].pt_price || '');
      setPricesPVPR(product.variants[0].pvpr_price || '');
    }
  };

  const handleSavePrices = async () => {
    if (!editingProductPrices) return;

    const pt = parseFloat(pricesPT);
    const pvpr = parseFloat(pricesPVPR);

    if (isNaN(pt) || isNaN(pvpr)) {
      toast.error('Por favor ingresa precios válidos');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API}/admin/products/${editingProductPrices.id}/prices`,
        { pt_price: pt, pvpr_price: pvpr },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success('Precios actualizados exitosamente');
      setEditingProductPrices(null);
      setPricesPT('');
      setPricesPVPR('');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar precios');
    }
  };

  const filteredProducts = products.filter(p => {
    // Defensive check: ensure we have a valid string before calling toLowerCase()
    const modelName = (p.model_name || p.model || p.display_name || '').toString();
    const reference = (p.variants && p.variants[0] && p.variants[0].reference) ? p.variants[0].reference.toString() : '';
    const searchLower = searchTerm.toLowerCase();
    
    // Allow searching by name OR reference
    return modelName.toLowerCase().includes(searchLower) || reference.toLowerCase().includes(searchLower);
  });

  const filteredProductsPrices = products.filter(p => {
    const modelName = (p.model_name || p.model || p.display_name || '').toString();
    const reference = (p.variants && p.variants[0] && p.variants[0].reference) ? p.variants[0].reference.toString() : '';
    const searchLower = searchTermPrices.toLowerCase();
    
    // Allow searching by name OR reference
    return modelName.toLowerCase().includes(searchLower) || reference.toLowerCase().includes(searchLower);
  });

  // Filter programming products by code OR name
  const filteredProgrammingProducts = programmingProducts.filter(p => {
    const modelName = (p.model_name || p.model || p.display_name || '').toString();
    const reference = (p.reference || '').toString();
    const codeLower = searchTermProgramming.toLowerCase();
    const nameLower = searchTermProgrammingName.toLowerCase();
    
    const matchesCode = !codeLower || reference.toLowerCase().includes(codeLower);
    const matchesName = !nameLower || modelName.toLowerCase().includes(nameLower);
    
    return matchesCode && matchesName;
  });

  const handleCompleteImages = async () => {
    if (!window.confirm('¿Buscar imágenes faltantes automáticamente desde hi-tecupdatefile.weebly.com?\n\nEsto puede tardar varios minutos.')) {
      return;
    }

    setCompletingImages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/complete-missing-images`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(`✓ Completado: ${response.data.updated} productos actualizados con imágenes`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al completar imágenes');
    } finally {
      setCompletingImages(false);
    }
  };

  // Upload ZIP images for SS27
  const handleUploadZipImages = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.zip')) {
      toast.error('Solo se permiten archivos .zip');
      return;
    }

    setUploadingZipImages(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/upload-ss27-images`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const data = response.data;
      toast.success(data.message || `✅ ${data.matched_images} imágenes mapeadas a ${data.products_updated} productos`);
      
      if (data.unmatched_count > 0) {
        toast.info(`ℹ️ ${data.unmatched_count} archivos sin coincidencia de referencia`, { duration: 5000 });
      }
      
      // Refresh programming products if loaded
      if (programmingProducts.length > 0) {
        fetchProgrammingProducts();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir ZIP de imágenes');
    } finally {
      setUploadingZipImages(false);
      e.target.value = '';
    }
  };

  // Fetch programming products for image management
  const fetchProgrammingProducts = async (season = 'SS27') => {
    setLoadingProgrammingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/programming-products?season=${season}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgrammingProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos de programación');
    } finally {
      setLoadingProgrammingProducts(false);
    }
  };

  // Edit programming product images
  const handleEditProgrammingImages = (product) => {
    setEditingProgrammingProduct(product);
    setProgrammingImageUrls((product.images || []).join('\n'));
  };

  // Save programming product images
  const handleSaveProgrammingImages = async () => {
    if (!editingProgrammingProduct) return;

    try {
      const token = localStorage.getItem('token');
      const urls = programmingImageUrls.split('\n').filter(url => url.trim() !== '');
      
      await axios.put(
        `${API}/programming/products/${editingProgrammingProduct.id}/images`,
        urls,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success('Imágenes actualizadas exitosamente');
      setEditingProgrammingProduct(null);
      setProgrammingImageUrls('');
      fetchProgrammingProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar imágenes');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      shipped: 'bg-green-100 text-green-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      shipped: 'Enviado',
      delivered: 'Entregado',
      cancelled: 'Cancelado'
    };
    return texts[status] || status;
  };

  const handlePrint = (order) => {
    setPrintingOrder(order);
    setTimeout(() => {
      window.print();
      setPrintingOrder(null);
    }, 100);
  };

  // Get unique clients from orders
  const uniqueClients = useMemo(() => {
    const clients = [...new Set(orders.map(o => o.business_name))];
    return clients.sort();
  }, [orders]);

  // Filtered orders with combined filters
  const filteredOrders = useMemo(() => {
    let filtered = [...orders];

    // Filter by client
    if (filterClient) {
      filtered = filtered.filter(order => order.business_name === filterClient);
    }

    // Filter by date range
    if (filterDateFrom) {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at);
        const fromDate = new Date(filterDateFrom);
        return orderDate >= fromDate;
      });
    }

    if (filterDateTo) {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at);
        const toDate = new Date(filterDateTo);
        toDate.setHours(23, 59, 59, 999); // Include full day
        return orderDate <= toDate;
      });
    }

    // Filter by reference
    if (filterReference) {
      filtered = filtered.filter(order => {
        return order.items.some(item => 
          item.reference?.toLowerCase().includes(filterReference.toLowerCase()) ||
          item.model_name?.toLowerCase().includes(filterReference.toLowerCase())
        );
      });
    }

    return filtered;
  }, [orders, filterClient, filterReference, filterDateFrom, filterDateTo]);

  // Clear all filters
  const clearFilters = () => {
    setFilterClient('');
    setFilterReference('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  // Get unique provinces from users
  const uniqueUserProvinces = useMemo(() => {
    const provinces = [...new Set(users.map(u => u.province).filter(Boolean))];
    return provinces.sort();
  }, [users]);

  // Filtered users
  const filteredUsers = useMemo(() => {
    let filtered = [...users];

    if (filterUserName) {
      filtered = filtered.filter(user =>
        user.business_name?.toLowerCase().includes(filterUserName.toLowerCase())
      );
    }

    if (filterContactName) {
      filtered = filtered.filter(user =>
        user.contact_name?.toLowerCase().includes(filterContactName.toLowerCase())
      );
    }

    if (filterUserProvince) {
      filtered = filtered.filter(user => user.province === filterUserProvince);
    }

    return filtered;
  }, [users, filterUserName, filterContactName, filterUserProvince]);

  // Clear user filters
  const clearUserFilters = () => {
    setFilterUserName('');
    setFilterContactName('');
    setFilterUserProvince('');
  };

  // Export users to Excel
  const exportUsersToExcel = () => {
    if (filteredUsers.length === 0) {
      toast.error('No hay usuarios para exportar');
      return;
    }

    const excelData = filteredUsers.map(user => ({
      'Código Cliente': user.customer_code || '-',
      'Nombre del Negocio': user.business_name,
      'Nombre del Responsable': user.contact_name || '-',
      'Email': user.email,
      'Teléfono': user.phone || '-',
      'Provincia': user.province || '-',
      'Estado': user.approved ? 'Aprobado' : 'Pendiente',
      'Fecha de Registro': new Date(user.created_at).toLocaleDateString('es-ES')
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    
    const colWidths = [
      { wch: 15 }, // Código Cliente
      { wch: 30 }, // Nombre del Negocio
      { wch: 25 }, // Nombre del Responsable
      { wch: 30 }, // Email
      { wch: 15 }, // Teléfono
      { wch: 20 }, // Provincia
      { wch: 12 }, // Estado
      { wch: 15 }  // Fecha de Registro
    ];
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');

    const filename = `Usuarios_HiTec_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success(`Archivo ${filename} descargado exitosamente`);
  };

  // Delete order
  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este pedido? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Pedido eliminado exitosamente');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar pedido');
    }
  };

  // Export to Excel with new format
  const exportToExcel = () => {
    if (filteredOrders.length === 0) {
      toast.error('No hay pedidos para exportar');
      return;
    }

    // Prepare data for Excel with new format
    const excelData = [];
    let totalPares = 0;
    let totalEuros = 0;
    
    filteredOrders.forEach(order => {
      order.items.forEach(item => {
        totalPares += item.quantity;
        totalEuros += item.price * item.quantity;
        
        excelData.push({
          'Nº Pedido HI-TEC': order.order_number,
          'Fecha': new Date(order.created_at).toLocaleDateString('es-ES'),
          'Cliente': order.business_name,
          'Email': order.user_email,
          'Referencia': item.reference,
          'MARCA': 'HI-TEC',
          'Producto': item.model_name,
          'Color': item.color,
          'Talla': item.size,
          'EAN': item.ean || '-',
          'Cantidad': item.quantity,
          'Precio Unitario': item.price.toFixed(2),
          'Subtotal': (item.price * item.quantity).toFixed(2),
          'Estado': getStatusText(order.status),
          'Observaciones': order.observations || '-',
          'Fecha Entrega': order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('es-ES') : '-'
        });
      });
    });

    // Add summary rows
    excelData.push({});
    excelData.push({
      'Nº Pedido HI-TEC': '',
      'Fecha': '',
      'Cliente': '',
      'Email': '',
      'Referencia': '',
      'MARCA': 'Total PARES',
      'Producto': '',
      'Color': '',
      'Talla': '',
      'EAN': '',
      'Cantidad': totalPares,
      'Precio Unitario': '',
      'Subtotal': '',
      'Estado': '',
      'Observaciones': '',
      'Fecha Entrega': ''
    });
    excelData.push({});
    excelData.push({
      'Nº Pedido HI-TEC': '',
      'Fecha': '',
      'Cliente': '',
      'Email': '',
      'Referencia': '',
      'MARCA': 'Total Pedido',
      'Producto': '',
      'Color': '',
      'Talla': '',
      'EAN': '',
      'Cantidad': totalPares,
      'Precio Unitario': '',
      'Subtotal': totalEuros.toFixed(2),
      'Estado': '',
      'Observaciones': '',
      'Fecha Entrega': ''
    });

    // Create workbook
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    // Set column widths
    const colWidths = [
      { wch: 20 }, // Nº Pedido HI-TEC
      { wch: 12 }, // Fecha
      { wch: 25 }, // Cliente
      { wch: 30 }, // Email
      { wch: 15 }, // Referencia
      { wch: 12 }, // MARCA
      { wch: 30 }, // Producto
      { wch: 20 }, // Color
      { wch: 8 },  // Talla
      { wch: 15 }, // EAN
      { wch: 10 }, // Cantidad
      { wch: 15 }, // Precio Unitario
      { wch: 12 }, // Subtotal
      { wch: 12 }, // Estado
      { wch: 30 }, // Observaciones
      { wch: 15 }  // Fecha Entrega
    ];
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pedidos');

    // Generate filename with current date
    const filename = `Pedidos_HiTec_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    // Download
    XLSX.writeFile(wb, filename);
    toast.success(`Archivo ${filename} descargado exitosamente`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/catalog" className="flex items-center space-x-2">
              <ArrowLeft className="w-5 h-5" />
              <span>Volver al catálogo</span>
            </Link>
            <Link to="/" className="flex items-center space-x-2">
              <ShoppingBag className="w-8 h-8 text-blue-600" />
              <h1 className="text-2xl font-bold text-slate-900">HI-TEC B2B</h1>
            </Link>
          </div>
        </div>
      </header>

      {/* Admin Dashboard */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Panel de Administración</h1>
          <p className="text-slate-600">Gestiona pedidos, usuarios y catálogo</p>
        </div>

        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="grid w-full grid-cols-8 mb-8">
            <TabsTrigger data-testid="tab-orders" value="orders">
              <Package className="w-4 h-4 mr-2" />
              Pedidos
            </TabsTrigger>
            <TabsTrigger data-testid="tab-users" value="users">
              <Users className="w-4 h-4 mr-2" />
              Usuarios
            </TabsTrigger>
            <TabsTrigger data-testid="tab-accounts" value="accounts">
              <Building2 className="w-4 h-4 mr-2" />
              Cuentas
            </TabsTrigger>
            <TabsTrigger data-testid="tab-catalog" value="catalog">
              <Upload className="w-4 h-4 mr-2" />
              Catálogo
            </TabsTrigger>
            <TabsTrigger data-testid="tab-programming" value="programming">
              <Calendar className="w-4 h-4 mr-2" />
              Programación
            </TabsTrigger>
            <TabsTrigger data-testid="tab-seasons" value="seasons">
              <Calendar className="w-4 h-4 mr-2" />
              Temporadas
            </TabsTrigger>
            <TabsTrigger data-testid="tab-images" value="images">
              <Image className="w-4 h-4 mr-2" />
              Imágenes
            </TabsTrigger>
            <TabsTrigger data-testid="tab-prices" value="prices">
              <DollarSign className="w-4 h-4 mr-2" />
              Precios
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              {/* Header with filters toggle and export */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Todos los Pedidos</h2>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowFilters(!showFilters)}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Filter className="w-4 h-4" />
                    Filtros
                    {(filterClient || filterReference || filterDateFrom || filterDateTo) && (
                      <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                        {[filterClient, filterReference, filterDateFrom, filterDateTo].filter(Boolean).length}
                      </span>
                    )}
                  </Button>
                  <Button
                    onClick={exportToExcel}
                    disabled={filteredOrders.length === 0}
                    className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Exportar Excel
                  </Button>
                </div>
              </div>

              {/* Filters Panel */}
              {showFilters && (
                <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Client Filter */}
                    <div>
                      <Label className="text-sm font-semibold mb-2">Cliente</Label>
                      <select
                        value={filterClient}
                        onChange={(e) => setFilterClient(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Todos los clientes</option>
                        {uniqueClients.map((client, idx) => (
                          <option key={idx} value={client}>{client}</option>
                        ))}
                      </select>
                    </div>

                    {/* Reference Filter */}
                    <div>
                      <Label className="text-sm font-semibold mb-2">Referencia/Producto</Label>
                      <Input
                        type="text"
                        placeholder="Buscar referencia..."
                        value={filterReference}
                        onChange={(e) => setFilterReference(e.target.value)}
                        className="w-full"
                      />
                    </div>

                    {/* Date From Filter */}
                    <div>
                      <Label className="text-sm font-semibold mb-2">Fecha Desde</Label>
                      <Input
                        type="date"
                        value={filterDateFrom}
                        onChange={(e) => setFilterDateFrom(e.target.value)}
                        className="w-full"
                      />
                    </div>

                    {/* Date To Filter */}
                    <div>
                      <Label className="text-sm font-semibold mb-2">Fecha Hasta</Label>
                      <Input
                        type="date"
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Filter Actions */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                    <p className="text-sm text-slate-600">
                      Mostrando <strong>{filteredOrders.length}</strong> de <strong>{orders.length}</strong> pedidos
                    </p>
                    {(filterClient || filterReference || filterDateFrom || filterDateTo) && (
                      <Button
                        onClick={clearFilters}
                        variant="ghost"
                        size="sm"
                        className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
                      >
                        <X className="w-4 h-4" />
                        Limpiar filtros
                      </Button>
                    )}
                  </div>
                </div>
              )}
              
              {loadingOrders ? (
                <p className="text-slate-600">Cargando pedidos...</p>
              ) : filteredOrders.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 text-lg">
                    {orders.length === 0 ? 'No hay pedidos todavía' : 'No se encontraron pedidos con los filtros aplicados'}
                  </p>
                  {orders.length > 0 && (
                    <Button
                      onClick={clearFilters}
                      variant="outline"
                      className="mt-4"
                    >
                      Limpiar filtros
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredOrders.map((order) => (
                    <div 
                      key={order.id} 
                      data-testid={`admin-order-${order.id}`}
                      className="border border-slate-200 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-slate-900">#{order.order_number}</h3>
                          <p className="text-sm text-slate-600">
                            {order.business_name} ({order.user_email})
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(order.created_at).toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                          {order.created_by_email && order.created_by_email !== order.user_email && (
                            <p className="text-xs text-amber-600 font-medium mt-1">
                              📝 Grabado por: {order.created_by_email} ({order.created_by_role || 'usuario'})
                            </p>
                          )}
                        </div>
                        <div className="text-right flex items-center gap-2">
                          <Button
                            onClick={() => setEditingOrder(order)}
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1"
                          >
                            <Edit className="w-4 h-4" />
                            Editar
                          </Button>
                          {user.role === 'admin' && (
                            <Button
                              onClick={() => handleDeleteOrder(order.id)}
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                              Eliminar
                            </Button>
                          )}
                          <Button
                            onClick={() => handlePrint(order)}
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1"
                          >
                            <Printer className="w-4 h-4" />
                            Imprimir
                          </Button>
                          <div>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                              {getStatusText(order.status)}
                            </span>
                            <p className="text-xl font-bold text-blue-600 mt-2">€{order.total.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                        {order.items.map((item, index) => (
                          <div key={index} className="flex justify-between text-sm">
                            <span className="text-slate-700">
                              {item.model_name} ({item.color}, T{item.size}) x{item.quantity}
                            </span>
                            <span className="text-slate-900 font-semibold">
                              €{(item.price * item.quantity).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            {/* Pending Users Section */}
            {pendingUsers.length > 0 && (
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl shadow-lg p-6 mb-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                  <span className="bg-yellow-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-lg">
                    {pendingUsers.length}
                  </span>
                  Usuarios Pendientes de Aprobación
                </h2>
                <p className="text-sm text-slate-700 mb-4">
                  Estos usuarios se han registrado y están esperando tu aprobación para acceder al catálogo completo.
                </p>
                {loadingPendingUsers ? (
                  <p className="text-slate-600">Cargando usuarios pendientes...</p>
                ) : (
                  <div className="space-y-3">
                    {pendingUsers.map((u) => (
                      <div 
                        key={u.id}
                        className="bg-white border border-yellow-300 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-slate-900">{u.business_name}</h3>
                            {u.contact_name && (
                              <p className="text-sm text-blue-600 font-medium">👤 {u.contact_name}</p>
                            )}
                            <p className="text-sm text-slate-600">{u.email}</p>
                            {u.phone && (
                              <p className="text-sm text-slate-600">📞 {u.phone}</p>
                            )}
                            {u.province && (
                              <p className="text-sm text-slate-600">📍 {u.province}</p>
                            )}
                            <p className="text-xs text-slate-500">
                              Registrado: {new Date(u.created_at).toLocaleDateString('es-ES', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                          <div className="flex space-x-2 ml-4">
                            <Button
                              onClick={() => handleApproveUser(u.id)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              size="sm"
                            >
                              ✓ Aprobar
                            </Button>
                            <Button
                              onClick={() => handleRejectUser(u.id)}
                              className="bg-red-600 hover:bg-red-700 text-white"
                              size="sm"
                            >
                              ✗ Rechazar
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* User Import Section */}
            <div className="mb-6">
              <UserImport onImportComplete={() => {
                fetchUsers();
                fetchPendingUsers();
              }} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Create User Form */}
              <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Crear Nuevo Usuario</h2>
                <p className="text-sm text-slate-600 mb-4">
                  Los usuarios creados manualmente son aprobados automáticamente.
                </p>
                <form onSubmit={handleCreateUser} className="space-y-4">
                  <div>
                    <Label htmlFor="business_name">Nombre del Negocio</Label>
                    <Input
                      id="business_name"
                      data-testid="admin-create-business-input"
                      value={newUser.business_name}
                      onChange={(e) => setNewUser({ ...newUser, business_name: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      data-testid="admin-create-email-input"
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Teléfono</Label>
                    <Input
                      id="phone"
                      data-testid="admin-create-phone-input"
                      type="tel"
                      value={newUser.phone}
                      onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                      required
                      className="mt-1"
                      placeholder="+34 600 000 000"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password">Contraseña</Label>
                    <Input
                      id="password"
                      data-testid="admin-create-password-input"
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      required
                      className="mt-1"
                      minLength={6}
                    />
                  </div>
                  <Button 
                    data-testid="admin-create-user-btn"
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    Crear Usuario
                  </Button>
                </form>
              </div>

              {/* Users List */}
              <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
                {/* Header with filters toggle and export */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">Todos los Usuarios</h2>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowUserFilters(!showUserFilters)}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <Filter className="w-4 h-4" />
                      Filtros
                      {(filterUserName || filterContactName || filterUserProvince) && (
                        <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                          {[filterUserName, filterContactName, filterUserProvince].filter(Boolean).length}
                        </span>
                      )}
                    </Button>
                    <Button
                      onClick={exportUsersToExcel}
                      disabled={filteredUsers.length === 0}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Excel
                    </Button>
                  </div>
                </div>

                {/* Filters Panel */}
                {showUserFilters && (
                  <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 mb-4">
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <Label className="text-sm font-semibold mb-2">Nombre del Negocio</Label>
                        <Input
                          type="text"
                          placeholder="Buscar negocio..."
                          value={filterUserName}
                          onChange={(e) => setFilterUserName(e.target.value)}
                          className="w-full"
                        />
                      </div>

                      <div>
                        <Label className="text-sm font-semibold mb-2">Nombre del Responsable</Label>
                        <Input
                          type="text"
                          placeholder="Buscar responsable..."
                          value={filterContactName}
                          onChange={(e) => setFilterContactName(e.target.value)}
                          className="w-full"
                        />
                      </div>

                      <div>
                        <Label className="text-sm font-semibold mb-2">Provincia</Label>
                        <select
                          value={filterUserProvince}
                          onChange={(e) => setFilterUserProvince(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Todas las provincias</option>
                          {uniqueUserProvinces.map((prov, idx) => (
                            <option key={idx} value={prov}>{prov}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                      <p className="text-sm text-slate-600">
                        Mostrando <strong>{filteredUsers.length}</strong> de <strong>{users.length}</strong> usuarios
                      </p>
                      {(filterUserName || filterContactName || filterUserProvince) && (
                        <Button
                          onClick={clearUserFilters}
                          variant="ghost"
                          size="sm"
                          className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
                        >
                          <X className="w-4 h-4" />
                          Limpiar filtros
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {loadingUsers ? (
                  <p className="text-slate-600">Cargando usuarios...</p>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">
                      {users.length === 0 ? 'No hay usuarios registrados' : 'No se encontraron usuarios con los filtros aplicados'}
                    </p>
                    {users.length > 0 && (
                      <Button
                        onClick={clearUserFilters}
                        variant="outline"
                        className="mt-4"
                        size="sm"
                      >
                        Limpiar filtros
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {filteredUsers.map((u) => (
                      <div 
                        key={u.id} 
                        data-testid={`user-item-${u.id}`}
                        className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-slate-900">{u.business_name}</h3>
                            {u.contact_name && (
                              <p className="text-sm text-blue-600 font-medium">👤 {u.contact_name}</p>
                            )}
                            <p className="text-sm text-slate-600">{u.email}</p>
                            {u.phone && (
                              <p className="text-sm text-slate-600">📞 {u.phone}</p>
                            )}
                            {u.province && (
                              <p className="text-sm text-slate-600">📍 {u.province}</p>
                            )}
                            <p className="text-xs text-slate-500 mt-1">
                              Registrado: {new Date(u.created_at).toLocaleDateString('es-ES')}
                            </p>
                            {u.approved ? (
                              <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                ✓ Aprobado
                              </span>
                            ) : (
                              <span className="inline-block mt-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                ⏳ Pendiente
                              </span>
                            )}
                          </div>
                          <Button
                            onClick={() => setEditingUser(u)}
                            variant="outline"
                            size="sm"
                            className="ml-4"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Catalog Tab */}
          <TabsContent value="catalog">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Upload PRICAT */}
              <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Subir PRICAT</h2>
                <p className="text-slate-600 mb-6">
                  Archivo Excel con información de productos (CÓDIGO, MODELO, FOTOS)
                </p>
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center">
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <label 
                    htmlFor="pricat-upload" 
                    className="cursor-pointer"
                  >
                    <Button 
                      data-testid="upload-pricat-btn"
                      type="button" 
                      disabled={uploadingPricat}
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => document.getElementById('pricat-upload').click()}
                    >
                      {uploadingPricat ? 'Subiendo...' : 'Seleccionar PRICAT (.xlsx)'}
                    </Button>
                  </label>
                  <input
                    id="pricat-upload"
                    data-testid="pricat-file-input"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleUploadPricat}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Upload Stock */}
              <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Subir Stock</h2>
                <p className="text-slate-600 mb-6">
                  Archivo CSV con stock actualizado (SOLO actualiza stock, no precios)
                </p>
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center">
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <label 
                    htmlFor="stock-upload" 
                    className="cursor-pointer"
                  >
                    <Button 
                      data-testid="upload-stock-btn"
                      type="button" 
                      disabled={uploadingStock}
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => document.getElementById('stock-upload').click()}
                    >
                      {uploadingStock ? 'Subiendo...' : 'Seleccionar Stock (.csv)'}
                    </Button>
                  </label>
                  <input
                    id="stock-upload"
                    data-testid="stock-file-input"
                    type="file"
                    accept=".csv"
                    onChange={handleUploadStock}
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            {/* Rebuild Stock Catalog from FTP */}
            <div className="mt-6 bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">🔁 Reconstruir Catálogo de Stock (FTP)</h2>
              <p className="text-slate-600 mb-4">
                Descarga el último CSV de stock de la carpeta FTP y reconstruye el catálogo de Stock para que muestre
                EXACTAMENTE el stock real, completando automáticamente <strong>imágenes, EANs, tallas y precios</strong> desde
                los datos oficiales de hi-tecupdatefile.weebly.com (todas las temporadas).
              </p>
              <Button
                data-testid="rebuild-stock-btn"
                onClick={handleRebuildStock}
                disabled={rebuildingStock}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {rebuildingStock ? '🔄 Reconstruyendo...' : '🔁 Reconstruir desde FTP ahora'}
              </Button>
              {rebuildingStock && (
                <p className="text-sm text-slate-600 mt-3">⏳ Descargando FTP y reconstruyendo el catálogo. Espera unos segundos...</p>
              )}
              <p className="text-xs text-slate-500 mt-3">
                Esto también ocurre automáticamente cada día a las 8:30 y 20:30 (hora de Madrid).
              </p>
            </div>
            <div className="mt-6 bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">🔍 Completar Imágenes Automáticamente</h2>
              <p className="text-slate-600 mb-4">
                Busca automáticamente imágenes faltantes en hi-tecupdatefile.weebly.com y las añade a los productos.
              </p>
              <Button
                onClick={handleCompleteImages}
                disabled={completingImages}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {completingImages ? '🔄 Buscando imágenes...' : '✨ Completar Imágenes Faltantes'}
              </Button>
              {completingImages && (
                <p className="text-sm text-slate-600 mt-3">
                  ⏳ Esto puede tardar varios minutos. Por favor espera...
                </p>
              )}
            </div>

            {/* Instructions */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h3 className="font-bold text-slate-900 mb-3">Instrucciones:</h3>
              <ul className="space-y-2 text-slate-700 text-sm">
                <li>1. Primero sube todos los archivos PRICAT para crear los productos base</li>
                <li>2. Luego sube el archivo de stock CSV para actualizar stock (solo stock, no precios)</li>
                <li>3. Usa "Completar Imágenes" para buscar automáticamente las imágenes que faltan</li>
                <li>3. Puedes actualizar el stock tantas veces como necesites</li>
                <li>4. Los cambios se reflejan inmediatamente en el catálogo</li>
              </ul>
            </div>
          </TabsContent>

          {/* Programming Tab */}
          <TabsContent value="programming">
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-2xl p-6 mb-6">
              <h2 className="text-3xl font-bold mb-2">Gestión de Programación</h2>
              <p className="text-blue-100">Administra productos de temporadas futuras (SS26, AW26, SS27, etc.)</p>
            </div>

            {/* Import SS27 (from committed data: EAN + tarifa + imágenes reutilizadas) */}
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 mb-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">🌞 Importar Temporada SS27</h2>
              <p className="text-slate-600 mb-4">
                Carga los 105 modelos de SS27 (tallas, EANs, precios y filtros) para hacer pedidos de programación.
                Reutiliza imágenes de códigos que ya existían en otras temporadas; los modelos nuevos quedan sin foto
                hasta que se publiquen.
              </p>
              <Button
                data-testid="import-ss27-btn"
                onClick={handleImportSS27}
                disabled={importingSS27}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {importingSS27 ? '🔄 Importando SS27...' : '🌞 Importar / Actualizar SS27'}
              </Button>
            </div>

            {/* Pasar temporada de programación a STOCK (genérico) */}
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 mb-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">📦➡️ Pasar temporada a Stock</h2>
              <p className="text-slate-600 mb-4">
                Cuando una temporada empieza a servirse, pásala de <strong>Programación</strong> a <strong>Stock disponible</strong>:
                deja de mostrarse como programación (se oculta su pestaña) y sus artículos aparecen en el catálogo de Stock
                automáticamente cuando estén en el CSV del FTP. Sirve para la temporada que corresponda cada vez (AW26, luego SS27, luego AW27…).
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <select
                  data-testid="season-to-migrate-select"
                  value={seasonToMigrate}
                  onChange={(e) => setSeasonToMigrate(e.target.value)}
                  className="px-3 py-2 border-2 border-gray-300 rounded-lg bg-white focus:border-blue-500 focus:outline-none"
                >
                  <option value="">— Selecciona temporada —</option>
                  {progSeasonsList.map((s) => (
                    <option key={s.season} value={s.season}>{s.season} ({s.product_count} productos)</option>
                  ))}
                </select>
                <Button
                  data-testid="migrate-season-btn"
                  onClick={handleMigrateSeason}
                  disabled={migratingSeason || !seasonToMigrate}
                  className="bg-green-700 hover:bg-green-800"
                >
                  {migratingSeason ? '🔄 Pasando a stock...' : '📦 Pasar a Stock'}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Upload Programming PRICAT */}
              <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Subir PRICAT Programación</h2>
                
                {/* Season Selector */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Temporada
                  </label>
                  <select
                    value={programmingSeason}
                    onChange={(e) => setProgrammingSeason(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg"
                  >
                    <option value="SS26">SS26 - Primavera/Verano 2026</option>
                    <option value="AW26">AW26 - Otoño/Invierno 2026</option>
                    <option value="SS27">SS27 - Primavera/Verano 2027</option>
                    <option value="AW27">AW27 - Otoño/Invierno 2027</option>
                  </select>
                </div>

                <p className="text-slate-600 mb-4 text-sm">
                  Archivo Excel con productos de programación (sin stock físico actual)
                </p>

                <div className="border-2 border-dashed border-blue-300 rounded-xl p-8 text-center bg-blue-50">
                  <Calendar className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <label htmlFor="programming-pricat-upload" className="cursor-pointer">
                    <Button 
                      type="button" 
                      disabled={uploadingProgrammingPricat}
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => document.getElementById('programming-pricat-upload').click()}
                    >
                      {uploadingProgrammingPricat ? 'Subiendo...' : `Subir PRICAT ${programmingSeason}`}
                    </Button>
                  </label>
                  <input
                    id="programming-pricat-upload"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleUploadProgrammingPricat}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Quick Links */}
              <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Acceso Rápido</h2>
                <div className="space-y-3">
                  <a
                    href="/programming"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg p-4 transition"
                  >
                    <div className="flex items-center">
                      <Calendar className="w-6 h-6 text-blue-600 mr-3" />
                      <div>
                        <p className="font-semibold text-slate-900">Ver Catálogo Programación</p>
                        <p className="text-sm text-slate-600">Ver productos SS26 como cliente</p>
                      </div>
                    </div>
                  </a>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="text-2xl mr-3">ℹ️</div>
                      <div>
                        <p className="font-semibold text-green-900 mb-2">Fechas de Servicio Automáticas</p>
                        <ul className="text-sm text-green-800 space-y-1">
                          <li>• SS26 → 15 Febrero 2026</li>
                          <li>• AW26 → 15 Agosto 2026</li>
                          <li>• SS27 → 15 Febrero 2027</li>
                          <li>• AW27 → 15 Agosto 2027</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Complete Images for Programming */}
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 mb-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">🔍 Completar Imágenes {programmingSeason}</h2>
              <p className="text-slate-600 mb-4">
                Busca automáticamente imágenes para productos de {programmingSeason} desde hi-tecupdatefile.weebly.com/ss26.html
              </p>
              <Button
                onClick={handleCompleteProgrammingImages}
                disabled={completingProgrammingImages}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {completingProgrammingImages ? '🔄 Buscando imágenes...' : `✨ Completar Imágenes ${programmingSeason}`}
              </Button>
              {completingProgrammingImages && (
                <p className="text-sm text-slate-600 mt-3">
                  ⏳ Esto puede tardar varios minutos. Por favor espera...
                </p>
              )}
            </div>

            {/* Instructions */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-bold text-slate-900 mb-3 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-amber-600" />
                Importante sobre Programación
              </h3>
              <ul className="space-y-2 text-slate-700 text-sm">
                <li>✅ Los productos de programación NO tienen stock físico actual (stock = 0)</li>
                <li>✅ Se muestran en catálogo separado: /programming</li>
                <li>✅ La fecha de servicio se calcula automáticamente según la temporada</li>
                <li>✅ Los clientes pueden hacer pedidos anticipados si están aprobados</li>
                <li>⚠️ Puedes desactivar productos desde la pestaña "Imágenes" si se cancelan</li>
              </ul>
            </div>
          </TabsContent>

          {/* Images Tab */}
          <TabsContent value="images">
            {/* SS27 ZIP Upload Section */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-2xl p-6 mb-6">
              <h2 className="text-2xl font-bold mb-2">📦 Subir Imágenes SS27 (ZIP)</h2>
              <p className="text-amber-100 mb-4">
                Sube un archivo ZIP con las fotos de productos SS27. Las imágenes se mapean automáticamente 
                por código de referencia (ej: <code className="bg-white/20 px-1 rounded">O108O090302002_CETUS_OLIVE.JPG</code> → producto O090302002).
              </p>
              <div className="flex items-center gap-4">
                <label htmlFor="zip-images-upload" className="cursor-pointer">
                  <Button 
                    type="button"
                    disabled={uploadingZipImages}
                    className="bg-white text-amber-600 hover:bg-amber-50"
                    onClick={() => document.getElementById('zip-images-upload').click()}
                  >
                    {uploadingZipImages ? '📤 Subiendo...' : '📁 Seleccionar ZIP de Imágenes'}
                  </Button>
                </label>
                <input
                  id="zip-images-upload"
                  data-testid="zip-images-upload"
                  type="file"
                  accept=".zip"
                  onChange={handleUploadZipImages}
                  className="hidden"
                />
                {uploadingZipImages && (
                  <span className="text-sm">⏳ Procesando imágenes...</span>
                )}
              </div>
            </div>

            {/* SS27 Programming Products Images */}
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 mb-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">🌞 Imágenes de Productos SS27</h2>
              
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-slate-700">
                  <strong>Productos de programación SS27.</strong> Busca por código de referencia O NOMBRE del producto.
                  Las imágenes subidas por ZIP aparecerán aquí automáticamente.
                </p>
              </div>

              {/* Search Bars - Code and Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Buscar por Código</Label>
                  <Input
                    type="text"
                    placeholder="Ej: O090302002..."
                    value={searchTermProgramming}
                    onChange={(e) => setSearchTermProgramming(e.target.value)}
                    onFocus={() => !programmingProducts.length && fetchProgrammingProducts()}
                    data-testid="search-programming-code"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Buscar por Nombre</Label>
                  <Input
                    type="text"
                    placeholder="Ej: CETUS, PAMIR, ULA..."
                    value={searchTermProgrammingName}
                    onChange={(e) => setSearchTermProgrammingName(e.target.value)}
                    onFocus={() => !programmingProducts.length && fetchProgrammingProducts()}
                    data-testid="search-programming-name"
                  />
                </div>
              </div>

              {/* Load Products Button */}
              {!programmingProducts.length && !loadingProgrammingProducts && (
                <div className="text-center py-8">
                  <Button 
                    onClick={() => fetchProgrammingProducts('SS27')}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="load-programming-products-btn"
                  >
                    Cargar Productos SS27
                  </Button>
                </div>
              )}

              {/* Loading State */}
              {loadingProgrammingProducts && (
                <div className="text-center py-8">
                  <p className="text-slate-600">Cargando productos SS27...</p>
                </div>
              )}

              {/* Programming Products List */}
              {programmingProducts.length > 0 && !editingProgrammingProduct && (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {filteredProgrammingProducts.length === 0 ? (
                    <p className="text-slate-600 text-center py-8">
                      No se encontraron productos con esos criterios
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-slate-500 mb-2">
                        Mostrando {filteredProgrammingProducts.length} de {programmingProducts.length} productos SS27
                      </p>
                      {filteredProgrammingProducts.map((product) => (
                        <div 
                          key={product.id}
                          data-testid={`prog-product-${product.id}`}
                          className="border border-slate-200 rounded-lg p-4 hover:border-amber-400 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-bold text-slate-900 mb-1">{product.model_name}</h3>
                              {product.reference && (
                                <p className="text-sm text-amber-600 font-semibold mb-1">
                                  REF: {product.reference}
                                </p>
                              )}
                              {product.color && (
                                <p className="text-xs text-slate-500 mb-1">Color: {product.color}</p>
                              )}
                              <p className="text-sm text-slate-600 mb-2">
                                {(product.images || []).length > 0 
                                  ? `✅ ${product.images.length} imagen(es)` 
                                  : '❌ Sin imágenes'}
                              </p>
                              {(product.images || []).length > 0 && (
                                <div className="flex space-x-2">
                                  {product.images.slice(0, 3).map((img, idx) => (
                                    <img 
                                      key={idx}
                                      src={img} 
                                      alt={product.model_name}
                                      className="w-16 h-16 object-cover rounded border border-slate-200"
                                      onError={(e) => e.target.style.display = 'none'}
                                    />
                                  ))}
                                  {product.images.length > 3 && (
                                    <div className="w-16 h-16 bg-slate-100 rounded border border-slate-200 flex items-center justify-center">
                                      <span className="text-xs text-slate-600">+{product.images.length - 3}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <Button
                              onClick={() => handleEditProgrammingImages(product)}
                              className="bg-amber-600 hover:bg-amber-700 ml-4"
                              size="sm"
                            >
                              Editar Imágenes
                            </Button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* Edit Programming Product Images Modal */}
              {editingProgrammingProduct && (
                <div className="border-2 border-amber-500 rounded-lg p-6 bg-amber-50">
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-slate-900">
                      Editando: {editingProgrammingProduct.model_name}
                    </h3>
                    {editingProgrammingProduct.reference && (
                      <p className="text-lg text-amber-600 font-semibold mt-1">
                        REF: {editingProgrammingProduct.reference}
                      </p>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <Label htmlFor="programming-image-urls" className="mb-2 block">
                      URLs de Imágenes (una por línea)
                    </Label>
                    <textarea
                      id="programming-image-urls"
                      value={programmingImageUrls}
                      onChange={(e) => setProgrammingImageUrls(e.target.value)}
                      className="w-full h-48 p-3 border border-slate-300 rounded-lg font-mono text-sm"
                      placeholder="https://ejemplo.com/imagen1.jpg&#10;https://ejemplo.com/imagen2.jpg"
                    />
                  </div>

                  {/* Preview */}
                  {programmingImageUrls.trim() && (
                    <div className="mb-4">
                      <Label className="mb-2 block">Vista Previa:</Label>
                      <div className="flex flex-wrap gap-2">
                        {programmingImageUrls.split('\n').filter(url => url.trim()).map((url, idx) => (
                          <img 
                            key={idx}
                            src={url.trim()} 
                            alt={`Preview ${idx + 1}`}
                            className="w-24 h-24 object-cover rounded border border-slate-300"
                            onError={(e) => {
                              e.target.style.border = '2px solid red';
                              e.target.alt = 'Error al cargar';
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex space-x-3">
                    <Button
                      onClick={handleSaveProgrammingImages}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Guardar Cambios
                    </Button>
                    <Button
                      onClick={() => {
                        setEditingProgrammingProduct(null);
                        setProgrammingImageUrls('');
                      }}
                      className="bg-slate-500 hover:bg-slate-600"
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Stock Products Images (existing functionality) */}
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">📦 Imágenes de Productos en Stock</h2>
              
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-slate-700">
                  <strong>Nota:</strong> Aquí puedes editar manualmente las imágenes de cada producto en stock. 
                  Los cambios se aplicarán inmediatamente.
                </p>
              </div>

              {/* Search Bar */}
              <div className="mb-6">
                <Input
                  type="text"
                  placeholder="Buscar por nombre o referencia (ej: O090147001)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => !products.length && fetchProducts()}
                  className="w-full"
                />
              </div>

              {/* Load Products Button */}
              {!products.length && !loadingProducts && (
                <div className="text-center py-8">
                  <Button 
                    onClick={fetchProducts}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Cargar Productos de Stock
                  </Button>
                </div>
              )}

              {/* Loading State */}
              {loadingProducts && (
                <div className="text-center py-8">
                  <p className="text-slate-600">Cargando productos...</p>
                </div>
              )}

              {/* Products List */}
              {products.length > 0 && !editingProduct && (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {filteredProducts.length === 0 ? (
                    <p className="text-slate-600 text-center py-8">
                      No se encontraron productos con ese nombre
                    </p>
                  ) : (
                    filteredProducts.map((product) => (
                      <div 
                        key={product.id}
                        className="border border-slate-200 rounded-lg p-4 hover:border-blue-400 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-slate-900 mb-1">{product.model_name}</h3>
                            {product.variants && product.variants[0] && product.variants[0].reference && (
                              <p className="text-sm text-blue-600 font-semibold mb-1">
                                REF: {product.variants[0].reference}
                              </p>
                            )}
                            <p className="text-sm text-slate-600 mb-2">
                              {product.images.length > 0 
                                ? `${product.images.length} imagen(es)` 
                                : 'Sin imágenes'}
                            </p>
                            {product.images.length > 0 && (
                              <div className="flex space-x-2">
                                {product.images.slice(0, 3).map((img, idx) => (
                                  <img 
                                    key={idx}
                                    src={img} 
                                    alt={product.model_name}
                                    className="w-16 h-16 object-cover rounded border border-slate-200"
                                    onError={(e) => e.target.style.display = 'none'}
                                  />
                                ))}
                                {product.images.length > 3 && (
                                  <div className="w-16 h-16 bg-slate-100 rounded border border-slate-200 flex items-center justify-center">
                                    <span className="text-xs text-slate-600">+{product.images.length - 3}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <Button
                            onClick={() => handleEditImages(product)}
                            className="bg-blue-600 hover:bg-blue-700 ml-4"
                            size="sm"
                          >
                            Editar Imágenes
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Edit Modal */}
              {editingProduct && (
                <div className="border-2 border-blue-500 rounded-lg p-6 bg-blue-50">
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-slate-900">
                      Editando: {editingProduct.model_name}
                    </h3>
                    {editingProduct.variants && editingProduct.variants[0] && editingProduct.variants[0].reference && (
                      <p className="text-lg text-blue-600 font-semibold mt-1">
                        REF: {editingProduct.variants[0].reference}
                      </p>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <Label htmlFor="image-urls" className="mb-2 block">
                      URLs de Imágenes (una por línea)
                    </Label>
                    <textarea
                      id="image-urls"
                      value={imageUrls}
                      onChange={(e) => setImageUrls(e.target.value)}
                      className="w-full h-48 p-3 border border-slate-300 rounded-lg font-mono text-sm"
                      placeholder="https://ejemplo.com/imagen1.jpg&#10;https://ejemplo.com/imagen2.jpg&#10;https://ejemplo.com/imagen3.jpg"
                    />
                    <p className="text-xs text-slate-600 mt-2">
                      Pega las URLs de las imágenes, una por línea. Puedes añadir hasta 7 imágenes.
                    </p>
                  </div>

                  {/* Preview */}
                  {imageUrls.trim() && (
                    <div className="mb-4">
                      <Label className="mb-2 block">Vista Previa:</Label>
                      <div className="flex flex-wrap gap-2">
                        {imageUrls.split('\n').filter(url => url.trim()).map((url, idx) => (
                          <img 
                            key={idx}
                            src={url.trim()} 
                            alt={`Preview ${idx + 1}`}
                            className="w-24 h-24 object-cover rounded border border-slate-300"
                            onError={(e) => {
                              e.target.style.border = '2px solid red';
                              e.target.alt = 'Error al cargar';
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex space-x-3">
                    <Button
                      onClick={handleSaveImages}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Guardar Cambios
                    </Button>
                    <Button
                      onClick={() => {
                        setEditingProduct(null);
                        setImageUrls('');
                      }}
                      className="bg-slate-500 hover:bg-slate-600"
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Prices Tab */}
          <TabsContent value="prices">
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Gestión de Precios</h2>
              
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-slate-700">
                  <strong>Nota:</strong> Los precios se aplican a todas las tallas del modelo/color seleccionado.
                  Los cambios son inmediatos en el catálogo.
                </p>
              </div>

              {/* Search Bar */}
              <div className="mb-6">
                <Input
                  type="text"
                  placeholder="Buscar por nombre o referencia (ej: O090147001)..."
                  value={searchTermPrices}
                  onChange={(e) => setSearchTermPrices(e.target.value)}
                  onFocus={() => !products.length && fetchProducts()}
                  className="w-full"
                />
              </div>

              {/* Load Products Button */}
              {!products.length && !loadingProducts && (
                <div className="text-center py-8">
                  <Button 
                    onClick={fetchProducts}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Cargar Productos
                  </Button>
                </div>
              )}

              {/* Loading State */}
              {loadingProducts && (
                <div className="text-center py-8">
                  <p className="text-slate-600">Cargando productos...</p>
                </div>
              )}

              {/* Products List */}
              {products.length > 0 && !editingProductPrices && (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {filteredProductsPrices.length === 0 ? (
                    <p className="text-slate-600 text-center py-8">
                      No se encontraron productos con ese nombre
                    </p>
                  ) : (
                    filteredProductsPrices.map((product) => (
                      <div 
                        key={product.id}
                        className="border border-slate-200 rounded-lg p-4 hover:border-blue-400 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-slate-900 mb-1">
                              {product.model_name || product.display_name}
                            </h3>
                            {product.variants && product.variants[0] && product.variants[0].reference && (
                              <p className="text-sm text-blue-600 font-semibold mb-1">
                                REF: {product.variants[0].reference}
                              </p>
                            )}
                            <div className="text-sm text-slate-600 space-y-1">
                              {product.variants && product.variants.length > 0 && (
                                <>
                                  <p>
                                    <strong>PT:</strong> €{product.variants[0].pt_price?.toFixed(2) || '0.00'}
                                  </p>
                                  <p>
                                    <strong>PVPR:</strong> €{product.variants[0].pvpr_price?.toFixed(2) || '0.00'}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {product.variants.length} tallas
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                          <Button
                            onClick={() => handleEditPrices(product)}
                            className="bg-blue-600 hover:bg-blue-700 ml-4"
                            size="sm"
                          >
                            Editar Precios
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Edit Modal */}
              {editingProductPrices && (
                <div className="border-2 border-blue-500 rounded-lg p-6 bg-blue-50">
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-slate-900">
                      Editando: {editingProductPrices.model_name || editingProductPrices.display_name}
                    </h3>
                    {editingProductPrices.variants && editingProductPrices.variants[0] && editingProductPrices.variants[0].reference && (
                      <p className="text-lg text-blue-600 font-semibold mt-1">
                        REF: {editingProductPrices.variants[0].reference}
                      </p>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label htmlFor="pt-price" className="mb-2 block">
                        Precio Tarifa (PT) €
                      </Label>
                      <Input
                        id="pt-price"
                        type="number"
                        step="0.01"
                        value={pricesPT}
                        onChange={(e) => setPricesPT(e.target.value)}
                        className="w-full"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <Label htmlFor="pvpr-price" className="mb-2 block">
                        Precio PVPR €
                      </Label>
                      <Input
                        id="pvpr-price"
                        type="number"
                        step="0.01"
                        value={pricesPVPR}
                        onChange={(e) => setPricesPVPR(e.target.value)}
                        className="w-full"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <p className="text-xs text-slate-600 mb-4">
                    Este precio se aplicará a todas las {editingProductPrices.variants?.length || 0} tallas del producto.
                  </p>

                  <div className="flex space-x-3">
                    <Button
                      onClick={handleSavePrices}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Guardar Cambios
                    </Button>
                    <Button
                      onClick={() => {
                        setEditingProductPrices(null);
                        setPricesPT('');
                        setPricesPVPR('');
                      }}
                      className="bg-slate-500 hover:bg-slate-600"
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Seasons Tab */}
          <TabsContent value="seasons">
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <SeasonsManager />
            </div>
          </TabsContent>

          {/* Accounts Tab */}
          <TabsContent value="accounts">
            <AccountsManager />
          </TabsContent>
        </Tabs>
      </main>

      {/* Print View - Hidden on screen, visible when printing */}
      {printingOrder && (
        <div className="hidden print:block">
          <PrintOrder order={printingOrder} />
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            fetchUsers();
            fetchPendingUsers();
          }}
        />
      )}

      {/* Edit Order Modal */}
      {editingOrder && (
        <EditOrderModal
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onSuccess={() => fetchOrders()}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
